/**
 * The Route tests tile of the plan's facts strip (user, 19/10/2026): the
 * test drops' count, the first one on both clocks ("First 🇩🇰 15:00 −1d ·
 * 🇦🇺 00:00"), and the status — today's progress, or, with the Route tests
 * strategy, amber when the test does not go before the send window (green
 * that it does while nothing has gone yet). A click opens the route test.
 * Shown only when the country has a route-test container.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import { FlaskConical } from 'lucide-react';

import { minutesToHHMM } from '../../../utils/format';
import { useCountryClock } from '../../shared/time/country-clock-context';
import type { FactStatus } from '../facts/generation-fact';
import { ClockLine, DkTime } from '../schedule/DkTime';
import { FactTile } from '../schedule/FactTile';
import type { RouteTestFact } from './route-test-fact';

const HINT =
  'A route test sends small test drops and ranks the routes on today’s results — its own drops, or each gateway’s numbers. Its drops go before the window opens — with the Route tests strategy the first must — so the day starts on the best route.';

/** A wrong window wins; a right one shows while nothing has gone yet; else today's progress. */
function statusOf(fact: RouteTestFact): FactStatus {
  if (fact.window && !fact.window.ok) return { text: fact.window.text, tone: 'warning' };
  if (fact.window?.ok && fact.progress.tone === 'muted' && !fact.off) return { text: fact.window.text, tone: 'success' };
  return fact.progress;
}

const FirstDrop: FC<{ fact: RouteTestFact; tz: string | null }> = ({ fact, tz }) => {
  const { flag, runDate } = useCountryClock();
  const count = fact.count > 1 ? `${fact.count} tests · ` : '';
  if (fact.awaitingPlan) return <>{count}First drop —</>;
  if (fact.firstMin === null) return <>{count}{fact.off ? 'Every route test is off' : 'Its schedule plans no drop today'}</>;
  return (
    <span className="inline-flex min-w-0 items-center gap-1.5">
      {count}First
      <ClockLine flag={tz ? '🇩🇰' : null}>
        <DkTime min={fact.firstMin} tz={tz} runDate={runDate} />
      </ClockLine>
      {tz ? (
        <>
          ·<ClockLine flag={flag}>{minutesToHHMM(fact.firstMin)}</ClockLine>
        </>
      ) : null}
    </span>
  );
};

export const RouteTestTile: FC<{ fact: RouteTestFact; tz: string | null; onOpen?: (id: string) => void }> = ({ fact, tz, onOpen }) => {
  const action = onOpen ? { label: 'Open the route test', onClick: () => onOpen(fact.openId) } : undefined;
  return (
    <FactTile icon={FlaskConical} label="Route tests" hint={HINT} action={action} detail={<FirstDrop fact={fact} tz={tz} />} status={statusOf(fact)} testId="fact-route-test">
      <span className={fact.off ? 'text-muted-foreground' : undefined} data-testid="route-test-fact">
        {fact.value}
      </span>
    </FactTile>
  );
};
